// src/pages/TeamTasks/TeamTasks.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, AlertTriangle, Plus, Sparkles, ListChecks, CheckCircle2 } from 'lucide-react';
import { tasksApi, Task } from '../../api/tasks';
import { visibilityApi, type TeamMember } from '../../api/todo';
import { useAuth } from '../../contexts/AuthContext';
import TaskCard from '../../components/TaskCard';
import TaskCreate from '../Task/TaskCreate';

const ELEVATED = new Set(['admin', 'owner', 'superadmin', 'founder', 'director']);
const MANAGER_ROLES = new Set(['manager', 'supervisor', 'team lead', 'hod', 'hr']);

function TeamTasks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const isAdmin = ELEVATED.has(role);
  const isManager = MANAGER_ROLES.has(role);
  const canFilter = isAdmin || isManager;

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeClosed, setIncludeClosed] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    if (!canFilter) return;
    visibilityApi.team().then((r) => setTeamMembers(r.data.items || [])).catch(() => {});
  }, [canFilter]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let items: Task[] = [];
      if (isAdmin) {
        const res = await tasksApi.list({
          assigned_to: selectedEmployee || undefined,
          status: statusFilter || undefined,
          include_closed: includeClosed,
          page_size: 200,
        });
        items = res.data.items || [];
      } else {
        const res = await tasksApi.getTeamTasks(includeClosed);
        items = res.data.items || [];
        if (selectedEmployee) {
          items = items.filter((t) => t.primary_assignee_id === selectedEmployee || t.created_by === selectedEmployee);
        }
        if (statusFilter) items = items.filter((t) => t.status === statusFilter);
      }
      setTasks(items);
    } catch (err: any) {
      setError(err.message || 'Failed to load team tasks');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedEmployee, statusFilter, includeClosed]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const openTasksCount = tasks.filter((t) => !['DONE', 'CANCELLED'].includes(t.status)).length;
  const overdueCount = tasks.filter((t) => t.is_overdue && !['DONE', 'CANCELLED'].includes(t.status)).length;

  if (loading) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="gt-loading-ring mx-auto mb-3" />
          <p className="gt-page-sub gt-serif-italic">Loading <em>team</em> tasks…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="gt-card p-8 max-w-md text-center" style={{ borderColor: 'rgba(220,38,38,0.18)' }}>
          <AlertTriangle className="w-9 h-9 mx-auto mb-3" style={{ color: 'var(--gt-dnd)' }} />
          <p className="gt-display" style={{ fontSize: 14 }}>{error}</p>
          <button onClick={loadTasks} className="gt-btn is-danger mt-4 mx-auto">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="gt-shell min-h-screen">
        <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
          <div className="gt-page-hero flex-col md:flex-row gap-4">
            <div>
              <div className="gt-eyebrow-row">
                <span className="gt-leaf" />
                <span className="gt-eyebrow">i. Collective</span>
              </div>
              <h1 className="gt-page-title">Team <em>Tasks</em></h1>
              <p className="gt-page-sub" style={{ marginTop: 6 }}>
                {isAdmin ? <>All tasks across the <em className="gt-serif-italic">organization</em>.</> : <>Tasks from your <em className="gt-serif-italic">team</em>.</>}
              </p>
            </div>
            <button onClick={() => setIsCreateModalOpen(true)} className="gt-btn is-primary is-lg">
              <Plus className="w-4 h-4" /> Create New Task
            </button>
          </div>

          {tasks.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="gt-stat">
                <div className="gt-stat-label"><ListChecks className="w-3.5 h-3.5" /> Total</div>
                <p className="gt-stat-value">{tasks.length}</p>
              </div>
              <div className="gt-stat">
                <div className="gt-stat-label"><CheckCircle2 className="w-3.5 h-3.5" /> Open</div>
                <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>{openTasksCount}</p>
              </div>
              <div className="gt-stat" style={{ borderColor: overdueCount > 0 ? 'rgba(220,38,38,0.18)' : undefined }}>
                <div className="gt-stat-label"><AlertTriangle className="w-3.5 h-3.5" /> Overdue</div>
                <p className="gt-stat-value" style={{ color: overdueCount > 0 ? '#991b1b' : undefined }}>{overdueCount}</p>
              </div>
            </div>
          )}

          <div className="gt-card p-4 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeClosed}
                onChange={(e) => setIncludeClosed(e.target.checked)}
                style={{ accentColor: 'var(--gt-primary)' }}
              />
              <span className="gt-page-sub">Include Closed</span>
            </label>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="gt-select"
              style={{ width: 'auto', padding: '8px 12px', fontSize: 12.5 }}
            >
              <option value="">All Statuses</option>
              {['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'BLOCKED', 'DONE', 'CANCELLED'].map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>

            {canFilter && teamMembers.length > 0 && (
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="gt-select"
                style={{ width: 'auto', padding: '8px 12px', fontSize: 12.5, minWidth: 180 }}
              >
                <option value="">All Employees</option>
                {teamMembers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.name || m.email || m.user_id}</option>
                ))}
              </select>
            )}

            <span className="ml-auto gt-pill is-ghost gt-numerals">
              {tasks.length} task{tasks.length !== 1 ? 's' : ''}
            </span>
          </div>

          {tasks.length === 0 ? (
            <div className="gt-card p-12 text-center">
              <div className="gt-empty">
                <div className="gt-empty-leaf"><Users className="w-6 h-6" /></div>
                <div className="gt-empty-title">Nothing matches</div>
                <div className="gt-page-sub">No tasks found for the <em className="gt-serif-italic">selected</em> filters.</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {tasks.map((task) => (
                <TaskCard key={task.id} task={task} onRefresh={loadTasks} />
              ))}
            </div>
          )}
        </div>
      </div>

      <TaskCreate
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => loadTasks()}
      />
    </>
  );
}

export default TeamTasks;
